import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Laptop, CheckCircle2, ArrowLeft, Building2, Truck, PackageSearch } from 'lucide-react';
import { toast } from 'sonner';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useDevicesByOwner, useCreateRecupero, useRequestQuipteamsRecupero } from '@/hooks/useRecuperos';
import { trpc } from '@/lib/trpc';
import type { Device } from '@/../../shared/types';

interface Props {
  open: boolean;
  onClose: () => void;
  bajaId: string;
  correo: string;
  employeeName?: string;
  country?: string;
}

type DeviceChoice = 'office' | 'pickup' | null;

interface DeviceState {
  device: Device;
  choice: DeviceChoice;
}

type DestinationOffice = 'buenosAires' | 'cordoba' | 'brasil' | 'colombia';

const OFFICE_OPTIONS: Array<{ key: DestinationOffice; label: string }> = [
  // i18n-ok: nombre propio de la oficina
  { key: 'buenosAires', label: 'Buenos Aires' },
  // i18n-ok: nombre propio de la oficina
  { key: 'cordoba', label: 'Córdoba' },
  // i18n-ok: nombre propio de la oficina
  { key: 'brasil', label: 'Brasil (São Paulo)' },
  // i18n-ok: nombre propio de la oficina
  { key: 'colombia', label: 'Colombia' },
];

interface PickupForm {
  destinationOffice: DestinationOffice | '';
  personalEmail: string;
  employeeId: string;
  address: string;
  addressExtra: string;
  country: string;
  phone: string;
  whenToContact: string;
  notes: string;
}

export function DeviceRecoveryPanel({ open, onClose, bajaId, correo, employeeName, country }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [deviceStates, setDeviceStates] = useState<DeviceState[]>([]);
  const [pickupForm, setPickupForm] = useState<PickupForm>({
    destinationOffice: '',
    personalEmail: '',
    employeeId: '',
    address: '',
    addressExtra: '',
    country: country || '',
    phone: '',
    whenToContact: '',
    notes: '',
  });
  const [results, setResults] = useState<Array<{ device: Device; actionId?: string; office: boolean }>>([]);

  const { data: devices, isLoading } = useDevicesByOwner(open ? correo : undefined);
  // Mail personal desde la planilla de altas (col C, match por correo winclap):
  // el pedido a QT sale a nombre del clapper, no de quien lo carga (Sol 04/09).
  const { data: personalData } = trpc.recuperos.getPersonalEmail.useQuery(
    { correoWinclap: correo },
    { enabled: open && !!correo, staleTime: 5 * 60_000, retry: false },
  );
  const createRecupero = useCreateRecupero();
  const requestQuipteams = useRequestQuipteamsRecupero();

  // 'Vendida a Clapper' = la compró — se informa pero no entra al recupero
  const isSold = (s?: string) => (s ?? '').trim().toLowerCase() === 'vendida a clapper';
  const soldDevices = (devices ?? []).filter((d) => isSold(d.status));

  // Initialize device states when data loads — useEffect to avoid setState during render
  useEffect(() => {
    if (devices && open) {
      setDeviceStates(devices.filter((d) => !isSold(d.status)).map((d) => ({ device: d, choice: null })));
    }
  }, [devices, open]);

  // Prefill del mail personal cuando llega de la planilla (editable)
  useEffect(() => {
    const personal = personalData?.personalEmail;
    if (open && personal) {
      setPickupForm((f) => (f.personalEmail ? f : { ...f, personalEmail: personal }));
    }
  }, [personalData, open]);

  // Reset when panel closes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setDeviceStates([]);
      setPickupForm({ destinationOffice: '', personalEmail: '', employeeId: '', address: '', addressExtra: '', country: country || '', phone: '', whenToContact: '', notes: '' });
      setResults([]);
    }
  }, [open, country]);

  const setChoice = (deviceId: string, choice: DeviceChoice) => {
    setDeviceStates((prev) =>
      prev.map((ds) => (ds.device.deviceId === deviceId ? { ...ds, choice } : ds))
    );
  };

  const allChosen = deviceStates.length > 0 && deviceStates.every((ds) => ds.choice !== null);
  const pickupDevices = deviceStates.filter((ds) => ds.choice === 'pickup');
  const officeDevices = deviceStates.filter((ds) => ds.choice === 'office');

  const handleContinue = () => {
    if (pickupDevices.length > 0) {
      setStep(2);
    } else {
      handleSubmitAll();
    }
  };

  const handleSubmitAll = async () => {
    try {
      const newResults: typeof results = [];

      for (const ds of officeDevices) {
        await createRecupero.mutateAsync({
          bajaId,
          correoWinclap: correo,
          employeeName: employeeName || correo,
          country,
          status: 'no_recovery',
          method: 'no_recovery',
          notes: `Equipo dejado en oficina: ${ds.device.modelo} (${ds.device.nroSerie})`,
        });
        newResults.push({ device: ds.device, office: true });
      }

      for (const ds of pickupDevices) {
        const result = await requestQuipteams.mutateAsync({
          bajaId,
          correoWinclap: correo,
          personalEmail: pickupForm.personalEmail || correo,
          employeeName: employeeName || correo,
          employeeId: pickupForm.employeeId || undefined,
          employeeAddress: pickupForm.address || undefined,
          employeeAddressExtra: pickupForm.addressExtra || undefined,
          employeePhone: pickupForm.phone || undefined,
          employeeWhenToContact: pickupForm.whenToContact || undefined,
          country: pickupForm.country || country || 'Argentina',
          serialNumber: ds.device.nroSerie,
          deviceModel: ds.device.modelo,
          deviceType: ds.device.categoria,
          // Reassign hacia la OFICINA elegida (fix 03/09): el backend resuelve
          // nombre/mail/dirección desde la config de Oficinas. Antes iba
          // hardcodeado "Winclap - Almacén" y QT lo tomaba como depósito.
          destinationOffice: pickupForm.destinationOffice as 'buenosAires' | 'cordoba' | 'brasil' | 'colombia',
          notes: pickupForm.notes || undefined,
        });
        newResults.push({ device: ds.device, office: false, actionId: result.actionId });
      }

      setResults(newResults);
      setStep(3);
    } catch (err: any) {
      toast.error(t('devices.recovery.error_processing', { message: err?.message || t('devices.recovery.error_unknown') }));
    }
  };

  const isPending = createRecupero.isPending || requestQuipteams.isPending;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Laptop className="h-5 w-5" />
            {step === 1 && t('devices.recovery.title_assigned')}
            {step === 2 && t('devices.recovery.title_pickup_form')}
            {step === 3 && t('devices.recovery.title_done')}
          </SheetTitle>
          <SheetDescription>
            {step === 1 && `${employeeName || correo} · ${correo}`}
            {step === 2 && t('devices.recovery.desc_pickup_form')}
            {step === 3 && t('devices.recovery.desc_done')}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* STEP 1 — device choices */}
          {step === 1 && (
            <>
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <WinclapLoader size={40} />
                </div>
              ) : deviceStates.length === 0 && soldDevices.length > 0 ? (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <PackageSearch className="h-10 w-10 text-muted-foreground" />
                  <p className="font-medium">{t('devices.recovery.all_sold_title')}</p>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {soldDevices.map((d) => (
                      <p key={d.deviceId}>{d.modelo} · {d.nroSerie}</p>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">{t('devices.recovery.all_sold_desc')}</p>
                  <Button variant="outline" onClick={onClose} className="mt-2">
                    {t('common.ui.close')}
                  </Button>
                </div>
              ) : !devices || devices.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <PackageSearch className="h-10 w-10 text-muted-foreground" />
                  <p className="font-medium">{t('devices.recovery.no_devices_title')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('devices.recovery.no_devices_desc')}
                  </p>
                  <Button variant="outline" onClick={onClose} className="mt-2">
                    {t('common.ui.close')}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {deviceStates.map(({ device, choice }) => (
                      <div key={device.deviceId} className="rounded-lg border p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <Laptop className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
                          <div>
                            <p className="font-medium leading-tight">{device.modelo}</p>
                            <p className="text-xs text-muted-foreground">
                              {device.nroSerie}
                              {device.quipteamsOrderId && (
                                <Badge variant="outline" className="ml-2 text-xs">Quipteams</Badge>
                              )}
                            </p>
                          </div>
                        </div>
                        <RadioGroup
                          value={choice || ''}
                          onValueChange={(v) => setChoice(device.deviceId, v as DeviceChoice)}
                          className="grid grid-cols-2 gap-2"
                        >
                          <div>
                            <RadioGroupItem value="office" id={`office-${device.deviceId}`} className="peer sr-only" />
                            <Label
                              htmlFor={`office-${device.deviceId}`}
                              className="flex items-center gap-2 cursor-pointer rounded-md border-2 border-muted p-3 hover:bg-accent peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-colors"
                            >
                              <Building2 className="h-4 w-4 shrink-0" />
                              <span className="text-sm leading-tight">{t('devices.recovery.office_choice')}</span>
                            </Label>
                          </div>
                          <div>
                            <RadioGroupItem value="pickup" id={`pickup-${device.deviceId}`} className="peer sr-only" />
                            <Label
                              htmlFor={`pickup-${device.deviceId}`}
                              className="flex items-center gap-2 cursor-pointer rounded-md border-2 border-muted p-3 hover:bg-accent peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-colors"
                            >
                              <Truck className="h-4 w-4 shrink-0" />
                              <span className="text-sm leading-tight">{t('devices.recovery.pickup_choice')}</span>
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    ))}
                  </div>
                  {soldDevices.length > 0 && (
                    <div className="rounded-lg bg-muted/50 border border-dashed p-3 text-sm text-muted-foreground">
                      <p className="font-medium text-foreground mb-1">{t('devices.recovery.sold_section_title')}</p>
                      {soldDevices.map((d) => (
                        <p key={d.deviceId} className="text-xs">{d.modelo} · {d.nroSerie}</p>
                      ))}
                    </div>
                  )}
                  <Button className="w-full" disabled={!allChosen} onClick={handleContinue}>
                    {t('devices.recovery.continue_btn')}
                  </Button>
                </>
              )}
            </>
          )}

          {/* STEP 2 — pickup form */}
          {step === 2 && (
            <>
              {pickupDevices.length > 0 && (
                <div className="rounded-lg bg-muted/50 p-3 space-y-1">
                  {pickupDevices.map(({ device }) => (
                    <div key={device.deviceId} className="flex items-center gap-2 text-sm">
                      <Laptop className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{device.modelo}</span>
                      <span className="text-muted-foreground">· {device.nroSerie}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-5">
                {/* Oficina destino: QuipTeams retira con el mail personal y lo
                    ENVÍA a esta oficina (reassign) — nunca a su depósito */}
                <div className="space-y-2">
                  <Label htmlFor="destinationOffice">
                    {t('devices.recovery.office_dest_label', '¿A qué oficina lo enviamos?')} <span className="text-red-500">*</span>
                  </Label>
                  <select
                    id="destinationOffice"
                    value={pickupForm.destinationOffice}
                    onChange={(e) => setPickupForm((f) => ({ ...f, destinationOffice: e.target.value as PickupForm['destinationOffice'] }))}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                  >
                    <option value="">{t('devices.recovery.office_dest_placeholder', 'Elegí la oficina destino…')}</option>
                    {OFFICE_OPTIONS.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    {t('devices.recovery.office_dest_hint', 'QuipTeams coordina el retiro con el mail personal y envía el equipo a esta oficina (con la dirección cargada en la tab Oficinas).')}
                  </p>
                </div>

                {/* QuipTeams gestiona el retiro end-to-end: para equipos de su
                    inventario solo hace falta cómo contactar al clapper */}
                <div className="space-y-2">
                  <Label htmlFor="personalEmail">
                    {t('devices.recovery.personal_email_label')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="personalEmail"
                    type="email"
                    placeholder="maria@gmail.com"
                    value={pickupForm.personalEmail}
                    onChange={(e) => setPickupForm((f) => ({ ...f, personalEmail: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {personalData?.personalEmail && pickupForm.personalEmail === personalData.personalEmail
                      ? t('devices.recovery.personal_email_prefilled', 'Prellenado desde la planilla de altas — el pedido a QuipTeams sale a nombre de este mail.')
                      : t('devices.recovery.personal_email_hint')}
                  </p>
                </div>

                <div className="rounded-lg border p-4 space-y-4">
                  <div>
                    <p className="text-sm font-medium">{t('devices.recovery.optional_section_title')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('devices.recovery.optional_section_hint')}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="country">{t('devices.recovery.country_label')}</Label>
                      <Input
                        id="country"
                        placeholder="Argentina"
                        value={pickupForm.country}
                        onChange={(e) => setPickupForm((f) => ({ ...f, country: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="employeeId">{t('devices.recovery.id_label')}</Label>
                      <Input
                        id="employeeId"
                        placeholder="12345678"
                        value={pickupForm.employeeId}
                        onChange={(e) => setPickupForm((f) => ({ ...f, employeeId: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="address">{t('devices.recovery.pickup_address_label')}</Label>
                    <Input
                      id="address"
                      placeholder={t('devices.recovery.av_corrientes_1234_caba')}
                      value={pickupForm.address}
                      onChange={(e) => setPickupForm((f) => ({ ...f, address: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="addressExtra">{t('devices.recovery.address_extra_label')}</Label>
                    <Input
                      id="addressExtra"
                      placeholder={t('devices.recovery.piso_3_dpto_b')}
                      value={pickupForm.addressExtra}
                      onChange={(e) => setPickupForm((f) => ({ ...f, addressExtra: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="phone">{t('devices.recovery.phone_label')}</Label>
                      <Input
                        id="phone"
                        placeholder="+54 11 1234-5678"
                        value={pickupForm.phone}
                        onChange={(e) => setPickupForm((f) => ({ ...f, phone: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="whenToContact">{t('devices.recovery.when_to_contact_label')}</Label>
                      <Input
                        id="whenToContact"
                        placeholder={t('devices.recovery.when_to_contact_placeholder')}
                        value={pickupForm.whenToContact}
                        onChange={(e) => setPickupForm((f) => ({ ...f, whenToContact: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="notes">
                    {t('devices.recovery.notes_label')}{' '}
                    <span className="text-muted-foreground text-xs">{t('devices.recovery.notes_optional')}</span>
                  </Label>
                  <Input
                    id="notes"
                    placeholder={t('devices.recovery.notes_placeholder')}
                    value={pickupForm.notes}
                    onChange={(e) => setPickupForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.ui.back')}
                </Button>
                <Button
                  className="flex-1"
                  disabled={isPending || !pickupForm.personalEmail || !pickupForm.destinationOffice}
                  onClick={handleSubmitAll}
                >
                  {isPending && <WinclapLoader size={16} className="mr-2" />}
                  {t('devices.recovery.confirm_send_btn')}
                </Button>
              </div>
            </>
          )}

          {/* STEP 3 — success */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-2 py-4">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
                <p className="text-lg font-semibold">{t('devices.recovery.devices_managed_title')}</p>
              </div>
              <div className="space-y-3">
                {results.map(({ device, office, actionId }) => (
                  <div key={device.deviceId} className="rounded-lg border p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Laptop className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{device.modelo}</span>
                      <span className="text-xs text-muted-foreground">· {device.nroSerie}</span>
                    </div>
                    {office ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                        <span>{t('devices.recovery.office_result')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm">
                        <Truck className="h-4 w-4 text-blue-500" />
                        <span>{t('devices.recovery.pickup_result')}</span>
                        {actionId && (
                          <Badge variant="outline" className="ml-1 font-mono text-xs">{actionId}</Badge>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Button className="w-full" onClick={onClose}>
                {t('common.ui.close')}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
